import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import isEmpty from 'lodash.isempty';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import Alert from '../../components/Alert';
import NavButton from '../../components/NavButton';
import NewApplicationDropdown from '../../components/NewApplicationDropdown';
import { useModuleStore } from '../../components/moduleStore';
import constants from '../../constants';

const referredOn = (request_dttm, statuses) =>
	statuses?.find(({ request_status }) => request_status === 'new')
		?.updated_dttm || request_dttm;

const MyWorklist = () => {
	const [limit /* setLimit */] = useState(11);
	let { referralType = 'outgoing', page = 1 } = useParams();
	const setServiceRequest = useModuleStore((state) => state.setServiceRequest);

	const dashboardQuery = useQuery({
		queryKey: ['worklist', referralType, page, limit],
		queryFn: async () => {
			const { data } = await axios.get(`/requests/worklist`, {
				params: {
					options: { referralType, skip: (page - 1) * (limit - 1), limit },
				},
			});
			return data;
		},
	});

	if (dashboardQuery.isFetched) {
		dashboardQuery.data.forEach((request) => {
			if (request?.external_211_service?.data) {
				try {
					request.recOrgName =
						request?.external_211_service?.data.locations[0].organization_name;
				} catch {
					request.recOrgName = 'NOT FOUND';
				}
			}
		});
	}

	const handleOpenApplicationModules = (requestId) => {
		setServiceRequest(dashboardQuery.data.find(({ id }) => id == requestId));
	};

	page = parseInt(page, 10);

	return (
		<>
			<div className="flex justify-end">
				<NewApplicationDropdown />
			</div>
			<div className="px-4 py-4 mt-4 bg-white rounded-lg">
				{dashboardQuery.isError && (
					<Alert message="There was a problem loading data!" />
				)}
				<table className="table table-zebra table-sm">
					<thead>
						<tr>
							<th>Referred On</th>
							<th>Referred By</th>
							<th>Client Name</th>
							{referralType === 'incoming' && <th>Referring Org</th>}
							{referralType === 'outgoing' && (
								<>
									<th>Receiving Org</th>
									<th>Assigned To</th>
								</>
							)}
							<th>Service</th>
							<th>Status</th>
						</tr>
					</thead>
					<tbody>
						{dashboardQuery.isFetching && (
							<tr>
								<td colSpan={5}>Loading...</td>
							</tr>
						)}
						{dashboardQuery.isFetched &&
							!dashboardQuery.isError &&
							dashboardQuery?.data.length === 0 && (
								<tr>
									<td colSpan={5}>No service requests found.</td>
								</tr>
							)}
						{dashboardQuery.isFetched &&
							!dashboardQuery.isError &&
							dashboardQuery.data.length > 0 &&
							dashboardQuery.data.slice(0, 10).map((serviceRequest) => (
								<tr key={`${serviceRequest.id}`}>
									<td>
										{format(
											new Date(
												referredOn(
													serviceRequest.request_dttm,
													serviceRequest.request_statuses,
												),
											),
											'Pp',
										)}
									</td>
									<td>
										{serviceRequest.created_by_coord
											? serviceRequest.created_by_coord.name_first +
												' ' +
												serviceRequest.created_by_coord.name_last
											: 'Self Referred'}
									</td>
									<td>
										{serviceRequest.client.name_first}{' '}
										{serviceRequest.client.name_last}
									</td>
									{referralType === 'incoming' && (
										<td>
											{serviceRequest?.coord_agency
												? serviceRequest.coord_agency.agency_name
												: 'Self Referred'}
										</td>
									)}
									{referralType === 'outgoing' && (
										<>
											<td>{serviceRequest.recOrgName}</td>
											<td>
												{serviceRequest?.assigned_to_coord ? (
													<>
														{serviceRequest.assigned_to_coord.name_first}{' '}
														{serviceRequest.assigned_to_coord.name_last}
													</>
												) : (
													'Unassigned'
												)}
											</td>
										</>
									)}
									<td>
										{serviceRequest.taxonomy_detail ||
											serviceRequest.external_211_service?.data?.name}
									</td>
									<td>
										{!isEmpty(serviceRequest.request_status) &&
											constants.status[serviceRequest.request_status]}
									</td>
									<td>
										<NavButton
											to={`/worklist/application/${serviceRequest.id}`}
											onClick={() =>
												handleOpenApplicationModules(serviceRequest.id)
											}
											title="View"
										/>
									</td>
								</tr>
							))}
					</tbody>
				</table>
				<div className="flex justify-end py-4 mt-4">
					<div className="grid grid-cols-2 w-72 join">
						<Link
							className="join-item btn btn-outline btn-secondary"
							disabled={page == 1}
							to={`/worklist/${referralType}/${page - 1}`}
						>
							Previous page
						</Link>
						<Link
							className="join-item btn btn-outline btn-secondary"
							disabled={dashboardQuery?.data?.length < limit - 1}
							to={`/worklist/${referralType}/${page + 1}`}
						>
							Next page
						</Link>
					</div>
				</div>
			</div>
		</>
	);
};

export default MyWorklist;
